import React, { useEffect, useRef, useState, useCallback } from "react";
import { APIOptions } from "../../services/common.service";
import { getUserInfo } from "../../services/user.service";
import TicketCard from "./TicketCard";
import { ScreenHeader } from "react-library";
import Loading from "../../components/common/spinner.tsx/Loading";
import { useParams } from "react-router-dom";
import { t } from "../../services/locale.service";

interface User {
  mobile?: string;
  name?: string;
}

interface Ticket {
  id: number;
  title: string;
  plotid: string;
  customername: string;
  farmermobile: string;
  cropid: string;
  created_at: string;
  updated_at: string;
  priority_id: number;
  article_count: number;
  article_ids: number[];
  state_id: number;
  // Add other fields as necessary
}
type UserPayload = {
  firstname: string;
  lastname?: string; // Marking lastname as optional  title
  mobile: string;   
  roles: string[];
};

const Tickets: React.FC = () => {
  const userToken = useRef<string>("");
  const [user, setUser] = useState<User | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zammadUserId, setzammadUserId] = useState<number>();

  const { plotId } = useParams<{ plotId: string }>();

  const getUserToken = useCallback(async () => {
    if (!userToken.current) {
      userToken.current = (await localStorage.getItem(
        "access_token"
      )) as string;
    }
    return userToken.current;
  }, []);

  const fetchUserInfo = useCallback(async () => {
    try {
      const options: APIOptions = {
        basePath: "https://dashboard.fyllo.in/api",
        sendTokenInUrl: true,
      };
      const url = "/farmusers/getfarmuserData";
      const res = await getUserInfo(url, options);
      setUser(res);
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  }, []);

  const createZammadUser = async (
    user: User,
    token: string
  ): Promise<number | null> => {
    try {
      const url = "https://core.fyllo.in/" + "zammad?path=users";

      if (!user.mobile) {
        alert("No phone number found");
        return null; // Return null explicitly
      }

      const [firstName, ...lastName] = (user.name ?? "").split(" ");

      const payload: UserPayload = {
        firstname: firstName,
        mobile: user.mobile,
        roles: ["Customer"],
      };

      // Only add lastname if it exists
      if (lastName.length > 0) {
        payload.lastname = lastName.join(" ");
      }

      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: new Headers({
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        }),
      }).then((res) => res.json());

      if (response.error) {
        throw new Error(response.error);
      }

      return response.id;
    } catch (error) {
      console.log("error: create zammad user", error);
      return null;
    }
  };

  const fetchZammadUserId = useCallback(
    async (mobile: string): Promise<number | null> => {
      try {
        const url = `https://core.fyllo.in/zammad?path=${encodeURIComponent(
          `users/search?query=mobile.keyword:${mobile}`
        )}`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${userToken.current}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch Zammad user ID: ${response.statusText}`
          );
        }

        const data = await response.json();
        // Check if a user was found
        const userId = data?.[0]?.id ?? null;
        if (userId) {
          setzammadUserId(userId);
          return userId; // Return the user ID if found
        }

        // If no user is found, create a new Zammad user
        const newUserId = await createZammadUser(user!, userToken.current!);
        if (newUserId) {
          setzammadUserId(newUserId);
        }
        return newUserId; // Return the new user ID after creation
      } catch (error) {
        console.error("Error fetching Zammad user ID:", error);
        return null;
      }
    },
    []
  );

  const getUserTickets = useCallback(async (zammadUserId: number) => {
    try {
 
      const filterQuery = `tickets/search?query=customer_id:${zammadUserId}`;
      const url = `https://core.fyllo.in/zammad?path=${encodeURIComponent(
        filterQuery
      )}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${userToken.current}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tickets: ${response.statusText}`);
      }

      const { assets } = await response.json();
      if (assets?.Ticket) {
        const ticketsArray = Object.values(assets.Ticket) as Ticket[];

        const filteredTickets = ticketsArray.filter(
          (ticket) => ticket.state_id === 2
        );

        const sortedTickets = filteredTickets.sort((a, b) => {
          return (
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          );
        });

        setTickets(sortedTickets);
      }
    } catch (error: unknown) {
      console.error("Error fetching user tickets:", error);

      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An unknown error occurred");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        await getUserToken();
        await fetchUserInfo();

        if (user?.mobile) {
          const zammadUserId = await fetchZammadUserId(user.mobile);
          if (zammadUserId) {
            await getUserTickets(zammadUserId);
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError("An unknown error occurred");
        }
      }
    };

    fetchData();
  }, [getUserToken, fetchUserInfo]);

  useEffect(() => {
    if (user?.mobile) {
      (async () => {
        const zammadUserId = await fetchZammadUserId(user.mobile as string);
        if (zammadUserId) {
          await getUserTickets(zammadUserId);
        } else {
          console.warn("No valid Zammad User ID found");
        }
      })();
    }
  }, [user, fetchZammadUserId, getUserTickets]);



  const handleBack = () => window.history.back();

  useEffect(() => {
    window.addEventListener("back-icon-press", handleBack);
    return () => {
      window.removeEventListener("back-icon-press", handleBack);
    };
  }, []);

  // const openTicket = (ticket: Ticket) => {
  //   console.log(ticket);
  // };

  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload = {
      title,
      group_id: 1,
      customer_id: zammadUserId,
      article: {
        content_type: "text/html",
        body: description,
        type: "note",
        internal: false,
      },
    };

    const isValidzammadUserId = typeof zammadUserId === "number";
    const isValidUserToken = typeof userToken.current === "string";
    const isValidPlotId = typeof plotId === "string";

    if (isValidzammadUserId && isValidUserToken && isValidPlotId) {
      const response = await postTicket(
        payload,
        zammadUserId,
        userToken.current,
        plotId
      );

      // Handle the response
      if (response) {
        setIsModalOpen(false);
      } else {
        console.error("Failed to create ticket.");
        setIsModalOpen(false);
      }
    } else {
      // Log error and alert user if input values are invalid
      console.error("Invalid input values:", {
        zammadUserId,
        userToken: userToken.current,
        plotId,
      });
      alert("Please provide valid user ID, token, and plot ID.");
    }
  };

  const postTicket = async (
    payload: {
      title: string;
      group_id: number;
      customer_id: number | undefined;
      article: object;
    },
    zammadUserId: number,
    fylloToken: string,
    plotId: string
  ) => {
    try {
      setLoading(true);
      const url = `https://core.fyllo.in/zammad?path=tickets&plotId=${plotId.trim()}`;

      // Make the POST request
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: new Headers({
          Authorization: `Bearer ${fylloToken}`,
          Accept: "application/json",
          "Content-Type": "application/json",
          From: zammadUserId.toString(),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error Response:", errorText);
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Get the raw response as text
      const textResponse = await response.text();

      // Try to parse the response as JSON
      try {
        const jsonResponse = JSON.parse(textResponse);
        return jsonResponse;
      } catch (error) {
        console.error("Failed to parse JSON:", error);
        setError("error in creating ticket");
        return null;
      }

    } catch (error) {
      setError("Ticket creation failed !");
      return null;
    } finally {
      setLoading(false);  
    }
  };

  return (
    <div>
      <ScreenHeader screenName={t("Raised_Service_Request")} />
      {/* {loading && <p style={{ textAlign: "center" }}>Loading....</p>} */}
      {loading && !error && <Loading message={t("Loading_Tickets")} />}
      {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}
      {!loading && !error && (
        <div className="ticket-container">
          {tickets?.length > 0 ? (
            tickets?.map((ticket, index) => (
              <TicketCard
                key={index}
                ticket={ticket}
                // onClick={() => openTicket(ticket as Ticket)}
              />
            ))
          ) : (
            <p>{t("No_tickets_found")}</p>
          )}
        </div>
      )}

      {!isModalOpen && (
        <div className="floating-button">
          <button onClick={() => setIsModalOpen(true)}>
            {t("Create_New_Request")}
          </button>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div
          className="ticket-modal-overlay"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="ticket-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>{t("Create_New_Request")}</h2>
            <form onSubmit={handleSubmit}>
              <label>
                {t("Title")}:
                <input
                  type="text"
                  name="title"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
              <label>
                {t("Description")}:
                <textarea
                  name= "Description"
                  required
                  value= {description}
                  onChange={(e) => setDescription(e.target.value)}
                ></textarea>
              </label>
              <div className="ticket_modal_buttons">
                <button type="button" onClick={() => setIsModalOpen(false)}>
                  {t("Close")}
                </button>
                <button type="submit" disabled={loading}>
                  {loading ?t("Submitting") : t("Submit")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;
